const ChallengeCircle = ({ 
  challengeType, 
  challengeActive, 
  value,
  label,
  sublabel,
  scale = 1,
  boltPreparationPhase = null,
  boltPreparationProgress = 0
}) => {
  // Color configurations with strong gradients
  const getColors = () => {
    if (challengeType === 'inhale') {
      return {
        primary: '#fbbf24',      // Amber
        secondary: '#b45309',    // Amber dark
        glow: '#fbbf2440',
        bg: 'from-amber-400 to-yellow-500'
      };
    }
    if (challengeType === 'exhale') {
      return {
        primary: '#38bdf8',      // Sky
        secondary: '#0369a1',    // Sky dark
        glow: '#38bdf840',
        bg: 'from-sky-400 to-blue-500'
      };
    }
    if (challengeType === 'apnea') {
      return {
        primary: '#34d399',      // Emerald
        secondary: '#047857',    // Emerald dark
        glow: '#34d39940',
        bg: 'from-emerald-400 to-green-500'
      };
    }
    if (challengeType === 'mbt') {
      return {
        primary: '#fb7185',      // Rose
        secondary: '#be123c',    // Rose dark
        glow: '#fb718540',
        bg: 'from-rose-400 to-pink-500'
      };
    }
    // BOLT / hold
    if (boltPreparationPhase === 'inhale') {
      return {
        primary: '#fbbf24',
        secondary: '#b45309',
        glow: '#fbbf2440',
        bg: 'from-amber-400 to-yellow-500'
      };
    }
    if (boltPreparationPhase === 'exhale') {
      return {
        primary: '#38bdf8',
        secondary: '#0369a1',
        glow: '#38bdf840',
        bg: 'from-sky-400 to-blue-500'
      };
    }
    return {
      primary: '#a78bfa',      // Violet
      secondary: '#5b21b6',    // Violet dark
      glow: '#a78bfa40',
      bg: 'from-violet-400 to-purple-500'
    };
  };

  const colors = getColors();

  // Calculate scale based on activity
  const calculateScale = () => {
    if (!challengeActive) return 1;
    return scale;
  };

  const circleScale = calculateScale();

  return (
    <div className="relative flex items-center justify-center h-48 w-48 sm:h-72 sm:w-72 shrink-0">
      {/* Outer glow */}
      <div 
        className="absolute rounded-full opacity-30 blur-2xl transition-all duration-100"
        style={{
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          transform: `scale(${circleScale * 1.2})`
        }}
      />
      
      {/* Main circle with gradient */}
      <div 
        className="relative rounded-full shadow-2xl flex items-center justify-center overflow-hidden transition-transform duration-100"
        style={{
          width: '180px',
          height: '180px',
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          transform: `scale(${circleScale})`,
          boxShadow: `0 0 40px ${colors.glow}, inset 0 0 30px rgba(255,255,255,0.2)`
        }}
      >
        {/* Glass shine effect */}
        <div 
          className="absolute inset-0 rounded-full"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)'
          }}
        />
        
        {/* Inner highlight ring */}
        <div 
          className="absolute rounded-full"
          style={{
            width: '160px',
            height: '160px',
            border: '2px solid rgba(255,255,255,0.3)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        />
        
        {/* Animated particles when active */}
        {challengeActive && (
          <div className="absolute inset-0 overflow-hidden rounded-full">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-white/40 animate-pulse"
                style={{
                  width: `${6 + (i % 3) * 3}px`,
                  height: `${6 + (i % 3) * 3}px`,
                  left: `${20 + (i * 12)}%`,
                  top: `${30 + (i * 8)}%`,
                  animationDelay: `${i * 0.15}s`,
                  animationDuration: '1s'
                }}
              />
            ))}
          </div>
        )}
        
        {/* Content */}
        <div className="relative text-center z-10 px-4">
          <div 
            className={`text-3xl sm:text-4xl font-bold text-white drop-shadow-lg ${challengeActive ? 'animate-pulse' : ''}`}
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}
          >
            {value}
          </div>
          {sublabel && (
            <p className="text-white/90 text-xs sm:text-sm mt-1 font-medium" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.3)' }}>
              {sublabel}
            </p>
          )}
        </div>
        
        {/* Bottom reflection */}
        <div 
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-1/3 rounded-b-full"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.15), transparent)'
          }}
        />
      </div>
      
      {/* Pulsing ring when active */}
      {challengeActive && (
        <div 
          className="absolute rounded-full border-2 animate-ping"
          style={{
            width: '180px',
            height: '180px',
            borderColor: colors.primary,
            opacity: 0.4
          }}
        />
      )}
    </div>
  );
};

export default ChallengeCircle;
